import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import cn from 'classnames';

import BrSearchBox from '@components/BrSearchBox';
import BrPaginator from '@components/BrPaginator';
import { CookieUtils } from '@components/UtilComponents/UtilFunctions';
import { CheckboxColumn } from './columns';
import { RowSelector, ColumnSelector, TableRenderer, Selectors, BRTABLE_CONSTANTS } from './components';
import 'react-virtualized/styles.css';
import './brTable.scss';

export default class BrTable extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    data: PropTypes.array.isRequired,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        // see BrColumn for full definition
        key: PropTypes.string.isRequired,
        column: PropTypes.func, // one of the column rendering function
        displayName: PropTypes.string, // header display
        // TODO: handling for date sort/search?
        type: PropTypes.oneOf(['string', 'number', 'checkboxEnum', 'date']),
        isSearchable: PropTypes.bool,
        isAdvanceSort: PropTypes.bool,
        isFilterable: PropTypes.bool,
      }),
    ).isRequired,
    minWidth: PropTypes.number, // min width the table will shrink to
    isLoading: PropTypes.bool,
    isGrid: PropTypes.bool,
    topRightElement: PropTypes.element,
    searchPlaceholder: PropTypes.string,
    onStateChange: PropTypes.func, // simple callback to let parent know state have changed
    // handles advance filter logic (rows:array, filterConfig:obj) => filteredRows:array
    onAdvanceFilter: PropTypes.func,

    // enable row select checkbox
    selectRowConfig: PropTypes.shape({
      batchSelectOptions: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.any.isRequired, // unique key
          title: PropTypes.string.isRequired, // option display text
          condition: PropTypes.func.isRequired, // data row will be passed as param  (row) => {}
        }),
      ),
      batchSelectActions: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.any.isRequired, // unique key
          title: PropTypes.string.isRequired, // option display text
          value: PropTypes.any, // if options not provided, this will become a standalone button
          onClick: PropTypes.func, // (selectedRows:array, item:{key, value}) => {}
          options: PropTypes.arrayOf(
            PropTypes.shape({
              value: PropTypes.any.isRequired,
              title: PropTypes.string.isRequired,
              onClick: PropTypes.func, // this will be called instead of onBatchSelectActions if provided.
              // (selectedRows:array, item:{key, value}) => {}
            }),
          ),
        }),
      ),
      onClickAction: PropTypes.func, // (selectedRows:array, item:{key, value}) => {}
    }),

    // enable column selector to display only those selected
    flexibleColumnConfig: PropTypes.shape({
      tableId: PropTypes.string.isRequired, // this is used for storing column configuration in cookie
      defaultColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
      // object of optional columns that can be hidden
      columnOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    }),

    // enable callback to refresh the data on page/sort/filter/search actions
    // by using this, the data param should contain only those that should be displayed
    ajaxConfig: PropTypes.shape({
      // ({page, numPerPage, query, sortConfig: {columnKey:string,orderBy:string}, filterConfig:{}}) =>
      onFetch: PropTypes.func.isRequired,
      totalDataLength: PropTypes.number.isRequired, // used for determining paginations
      defaultSortColumnKey: PropTypes.string,
      defaultSortOrderBy: PropTypes.oneOf([BRTABLE_CONSTANTS.ASC, BRTABLE_CONSTANTS.DESC, BRTABLE_CONSTANTS.NONE]),
    }),
  };

  static defaultProps = {
    isLoading: false,
    isGrid: false,
    minWidth: 600,
  };

  constructor(props) {
    super(props);
    this.state = this.getCleanTableState(props); // may use initial props
  }

  getCleanTableState = (props) => {
    const { ajaxConfig, selectRowConfig, flexibleColumnConfig, columns } = props;

    const isAjax = !!ajaxConfig;
    const isRowSelectable = !!selectRowConfig;
    const isColFlexible = !!flexibleColumnConfig;

    const allColumns = [...columns];
    const displayColumns = !isColFlexible
      ? columns.filter((col) => !!col.column).map((col) => col.key)
      : CookieUtils.getCookie(flexibleColumnConfig.tableId) || [...flexibleColumnConfig.defaultColumns];
    const defaultColumns = !isColFlexible ? [] : [...flexibleColumnConfig.defaultColumns];

    // add row selector checkbox column
    if (isRowSelectable) {
      const key = '__checkbox__';
      allColumns.unshift({
        key,
        displayName: ' ',
        column: CheckboxColumn,
        columnConfig: { onClick: this.handleCheckRow },
      });
      defaultColumns.unshift(key);
      if (!displayColumns.includes(key)) {
        displayColumns.unshift(key);
      }
    }

    const newState = {
      page: 1,
      numPerPage: 10,
      selectedRows: [],
      query: props.query || '',
      allColumns,
      defaultColumns,
      displayColumns,
      isAjax,
      isRowSelectable,
      isColFlexible,
      columnMap: allColumns.reduce((pv, col) => {
        pv[col.key] = col;
        return pv;
      }, {}),
      filterConfig: {},
      sortConfig: {
        columnKey: (isAjax && ajaxConfig.defaultSortColumnKey) || '',
        orderBy: (isAjax && ajaxConfig.defaultSortOrderBy) || '',
      },
    };
    return newState;
  };

  componentDidMount() {
    const { onStateChange } = this.props;
    if (onStateChange) {
      onStateChange(this.state);
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    // setState -> no rerender
    // this step is will not be invoked by this.setState
    if (nextProps.data !== this.props.data) {
      // eslint-disable-next-line no-console
      console.debug('data changed!!!');
      this.setState({ selectedRows: [] });
      this.forceUpdateTable();
    }

    if (nextProps.query !== this.props.query) {
      this.setState({ query: nextProps.query });
    }

    // in case where table is swapped by prop update, we need to reset columns and other configs
    const { flexibleColumnConfig } = nextProps;
    if (flexibleColumnConfig && flexibleColumnConfig.tableId !== this.props.flexibleColumnConfig.tableId) {
      this.setState(this.getCleanTableState(nextProps));
      this.forceUpdateTable();
    }
  }

  handleTableLoaded = (tableDom) => { this.tableDom = tableDom; };
  forceUpdateTable = () => this.tableDom && this.tableDom.recomputeGridSize();
  handleQueryChange = (query) => this.handleViewChanges({ type: 'queryChange', query });
  handleQuerySubmit = (query) => this.handleViewChanges({ type: 'querySubmit', query });
  handleChangePage = (page) => this.handleViewChanges({ type: 'page', page });
  handleChangeNumPerPage = (numPerPage) => this.handleViewChanges({ type: 'numPerPage', numPerPage });

  handleSortTable = (newColumnKey, newOrderBy) => {
    const {
      sortConfig: { columnKey, orderBy },
    } = this.state;
    const nextSortConfig = { columnKey: newColumnKey, orderBy: newOrderBy || BRTABLE_CONSTANTS.ASC };
    // if newOrderBy not specified, simply rotate through from ASC, DESC, to none
    if (columnKey === newColumnKey && !newOrderBy) {
      if (orderBy === BRTABLE_CONSTANTS.ASC) {
        nextSortConfig.orderBy = BRTABLE_CONSTANTS.DESC;
      } else {
        nextSortConfig.column = '';
      }
    }
    this.handleViewChanges({ type: 'sortConfig', sortConfig: nextSortConfig });
  };

  handleSetFilter = (columnKey, filter) => {
    const newConfig = { ...this.state.filterConfig };
    if (!filter) {
      delete newConfig[columnKey];
    } else {
      newConfig[columnKey] = filter;
    }
    this.handleViewChanges({ type: 'filterConfig', filterConfig: newConfig });
  };

  handleColumnUpdate = (displayColumns) => {
    // generate new displayColumns based on the order of state.allColumns
    const newDisplayColumns = this.state.allColumns
      .filter((col) => displayColumns.includes(col.key))
      .map((col) => col.key);
    CookieUtils.setCookie(this.props.flexibleColumnConfig.tableId, newDisplayColumns);
    this.handleViewChanges({ type: 'displayColumns', displayColumns: newDisplayColumns });
  };

  handleCheckRow = (row, isCheck) => {
    const { selectedRows } = this.state;
    if (isCheck) {
      this.setState({ selectedRows: [...selectedRows, row] });
    } else {
      // eslint-disable-next-line eqeqeq
      this.setState({ selectedRows: selectedRows.filter((r) => r != row) });
    }
    this.forceUpdateTable();
  };

  handleBatchSelect = (condition) => {
    const dataset = this.state.isAjax
      ? Selectors.hideAnimationDataSelector({ ...this.props, ...this.state })
      : Selectors.displayDataSelector({ ...this.props, ...this.state, filterHandler: this.props.onAdvanceFilter });
    this.setState({ selectedRows: dataset.filter((item) => condition(item)) });
    this.forceUpdateTable();
  };

  handleViewChanges = ({ type, query, page, numPerPage, sortConfig, filterConfig, displayColumns }) => {
    const { ajaxConfig, onStateChange } = this.props;
    const {
      isAjax,
      page: prevPage,
      selectedRows: prevSelectedRows,
      query: prevQuery,
      numPerPage: prevNumPerPage,
      sortConfig: prevSortConfig,
      filterConfig: prevFilterConfig,
      displayColumns: prevDisplayColumns,
    } = this.state;
    // compute new states
    const newState = {
      page: page || 1,
      selectedRows: [],
      query: query !== undefined ? query : prevQuery, // since query can be empty string, compare with undefined
      numPerPage: numPerPage || prevNumPerPage,
      sortConfig: sortConfig || prevSortConfig,
      filterConfig: filterConfig || prevFilterConfig,
      displayColumns: displayColumns || prevDisplayColumns,
    };

    if (isAjax) {
      if (['queryChange', 'displayColumns'].includes(type)) {
        // for ajax call, queryChange/displayColumns shouldn't trigger table change/ajax call
        newState.page = prevPage;
        newState.selectedRows = prevSelectedRows;
      } else if (ajaxConfig.onFetch) {
        ajaxConfig.onFetch(newState);
      }
    }

    this.setState(newState);
    this.forceUpdateTable();
    if (onStateChange) {
      onStateChange(newState);
    }
  };

  render() {
    const {
      className,
      data,
      minWidth,
      searchPlaceholder,
      topRightElement,
      ajaxConfig,
      selectRowConfig,
      flexibleColumnConfig,
      isLoading,
      isGrid,
      onAdvanceFilter,
    } = this.props;
    const {
      columnMap,
      defaultColumns,
      displayColumns,
      page,
      numPerPage,
      query,
      isAjax,
      isRowSelectable,
      selectedRows,
      sortConfig,
      filterConfig,
    } = this.state;

    const classname = cn('br-table', className);

    const selectorProps = {
      data,
      page,
      numPerPage,
      query,
      columnMap,
      sortConfig,
      filterConfig,
      filterHandler: onAdvanceFilter,
    };
    const [totalDataLength, displayData] = isAjax
      ? [ajaxConfig.totalDataLength, Selectors.hideAnimationDataSelector({ data, displayColumns })]
      : [Selectors.advancedFilteredDataSelector(selectorProps).length, Selectors.displayDataSelector(selectorProps)];

    return (
      <div className={classname}>
        <div className="flexrow">
          <BrSearchBox
            query={query}
            placeholder={searchPlaceholder}
            onQuerySubmit={this.handleQuerySubmit}
            onQueryChange={this.handleQueryChange}
            isAjaxSearch={isAjax}
          />
          <div className="flexdiv" />
          {topRightElement}
        </div>
        <div className={(selectRowConfig || flexibleColumnConfig) ? 'flexrow' : undefined}>
          {selectRowConfig && (
            <RowSelector
              selectedRows={selectedRows}
              selectableLength={displayData.length}
              onHandleBatchSelect={this.handleBatchSelect}
              selectRowConfig={selectRowConfig}
            />
          )}
          <div className="flexdiv" />
          {flexibleColumnConfig && (
            <ColumnSelector
              columnMap={columnMap}
              displayColumns={displayColumns}
              defaultColumns={defaultColumns}
              columnOptions={flexibleColumnConfig.columnOptions}
              onUpdateDisplayColumns={this.handleColumnUpdate}
            />
          )}
        </div>
        <TableRenderer
          columnMap={columnMap}
          displayColumns={displayColumns}
          displayData={displayData}
          minWidth={minWidth}
          isRowSelectable={isRowSelectable}
          sortConfig={sortConfig}
          filterConfig={filterConfig}
          selectedRows={selectedRows}
          isLoading={isLoading}
          isGrid={isGrid}
          onTableLoaded={this.handleTableLoaded}
          onSortClicked={this.handleSortTable}
          onSetFilter={this.handleSetFilter}
        />
        <BrPaginator
          currentPage={page}
          numPerPage={numPerPage}
          totalCount={totalDataLength}
          onChangePage={this.handleChangePage}
          onChangeNumPerPage={this.handleChangeNumPerPage}
        />
      </div>
    );
  }
}
